import type { JSX } from 'react';

import {
  KATEGORIENAME,
  type Absagehinweis,
  type Kategorie,
  type Listenansicht,
  type Listenstand,
  type Zielstand,
  type Zustaendigkeit,
} from '../api/support';
import { alsTag, alsZeitpunkt } from '../datum';
import { markenklasse } from '../marken';

/**
 * WAS DIE LISTE DER VORGÄNGE JE ZEILE ZUR BEARBEITUNG ZEIGT (Auftrag CRM-178).
 *
 * Zuständig, Fälligkeit, Kategorie, die Restzeit des nächsten offenen Ziels
 * und die beiden Markierungen, die man sehen muss, bevor man die Nachricht
 * öffnet: eine Terminabsage (SUP-48) und dass jemand anderes den Vorgang
 * gerade offen hat (SUP-22). Eigene Datei, damit die Liste in
 * `Posteingang.tsx` schmal bleibt.
 */

/** SUP-18: die drei Sichten der Liste, in der Folge des Umschalters. */
export const ANSICHTEN: readonly { readonly wert: Listenansicht; readonly text: string }[] = [
  { wert: 'meine', text: 'Meine' },
  { wert: 'warteschlange', text: 'Warteschlange' },
  { wert: 'alle', text: 'Alle' },
];

/**
 * Eine Dauer für die Restzeit: unter einer Stunde in Minuten, bis zwei Tage
 * in Stunden, darüber in Tagen. Abgerundet — „noch 3 Std." verspricht nie
 * mehr Zeit, als da ist.
 */
export function dauer(minuten: number, fall: 'noch' | 'seit'): string {
  const m = Math.abs(minuten);
  if (m < 60) return `${String(Math.max(1, Math.floor(m)))} Min.`;
  if (m < 48 * 60) return `${String(Math.floor(m / 60))} Std.`;
  const tage = Math.floor(m / (24 * 60));
  if (fall === 'seit') return `${String(tage)} ${tage === 1 ? 'Tag' : 'Tagen'}`;
  return `${String(tage)} ${tage === 1 ? 'Tag' : 'Tage'}`;
}

/**
 * SUP-25: das nächste offene Ziel — erst die Reaktion, nach der ersten
 * Antwort die Lösung. Ein erreichtes Ziel zählt nicht mehr.
 */
export function naechstesZiel(
  b: Pick<Listenstand, 'reaktion' | 'loesung'>,
): { readonly art: 'Reaktion' | 'Lösung'; readonly ziel: Zielstand } | null {
  if (b.reaktion !== null && b.reaktion.erreichtAm === null) return { art: 'Reaktion', ziel: b.reaktion };
  if (b.loesung !== null && b.loesung.erreichtAm === null) return { art: 'Lösung', ziel: b.loesung };
  return null;
}

/** Minuten bis zum Ziel; fehlt die Angabe des Servers, aus `bis` gerechnet. */
function restMinuten(z: Zielstand): number {
  return z.restMinuten ?? Math.floor((Date.parse(z.bis) - Date.now()) / 60_000);
}

/**
 * SUP-25: „Reaktion noch 3 Std." oder rot „Lösung überschritten seit 2 Std.".
 * Die letzte Stunde vor dem Ziel steht gelb da — dann ist es Zeit.
 */
export function Restzeit({ stand }: { readonly stand: Pick<Listenstand, 'reaktion' | 'loesung'> }): JSX.Element | null {
  const naechstes = naechstesZiel(stand);
  if (naechstes === null) return null;
  const rest = restMinuten(naechstes.ziel);
  const ueberschritten = naechstes.ziel.ueberschritten || rest < 0;
  const text = ueberschritten ? `überschritten seit ${dauer(rest, 'seit')}` : `noch ${dauer(rest, 'noch')}`;
  const bis = `${naechstes.art}sziel bis ${alsZeitpunkt(naechstes.ziel.bis) ?? naechstes.ziel.bis}`;
  return (
    <span
      className={ueberschritten ? markenklasse('sackgasse') : rest < 60 ? markenklasse('aufmerksamkeit') : 'hilfe'}
      title={bis}
    >
      {naechstes.art} {text}
    </span>
  );
}

/**
 * SUP-18, SUP-26: Person, Team oder „Warteschlange". Ist die Person
 * abwesend, steht dabei, wer sie gerade vertritt (BEN-10).
 */
export function Zustaendig({ zustaendigkeit: z }: { readonly zustaendigkeit: Zustaendigkeit }): JSX.Element {
  if (z.benutzer !== null) {
    return (
      <>
        <span className="liste__einzeilig">{z.benutzername ?? 'eine Person'}</span>
        {/* SUP-26: offene Vorgänge Abwesender sind sichtbar umgeleitet. */}
        {z.vertretenDurch !== null && (
          <>
            <br />
            <span className="hilfe">vertreten durch {z.vertreterName ?? 'die Vertretung'}</span>
          </>
        )}
      </>
    );
  }
  if (z.rolle !== null) {
    return (
      <>
        <span className="liste__einzeilig">{z.rollenname ?? z.rolle}</span>
        <br />
        <span className="hilfe">Team</span>
      </>
    );
  }
  return <span className={markenklasse('laufend')}>Warteschlange</span>;
}

/**
 * SUP-26: In „Meine" stehen auch die Vorgänge der Kollegen, die man gerade
 * vertritt — mit dem Namen dessen, für den man einspringt.
 */
export function Vertretungshinweis({
  zustaendigkeit: z,
  ansicht,
}: {
  readonly zustaendigkeit: Zustaendigkeit;
  readonly ansicht: Listenansicht;
}): JSX.Element | null {
  if (ansicht !== 'meine' || z.benutzer === null || z.vertretenDurch === null) return null;
  return <span className={markenklasse('aufmerksamkeit')}>Vertretung für {z.benutzername ?? 'eine Kollegin oder einen Kollegen'}</span>;
}

/**
 * SUP-14: die gesetzte Kategorie — oder, solange keine gesetzt ist, der
 * Vorschlag der Regel, kursiv und als Vorschlag benannt.
 */
export function Kategorieangabe({
  kategorie,
  vorschlag,
}: {
  readonly kategorie: Kategorie | null;
  readonly vorschlag: Kategorie | null;
}): JSX.Element {
  if (kategorie !== null) return <span className="liste__einzeilig">{KATEGORIENAME[kategorie]}</span>;
  if (vorschlag !== null) {
    return (
      <em className="hilfe" title="Vorschlag aus dem Inhalt der Nachricht — noch nicht bestätigt">
        Vorschlag: {KATEGORIENAME[vorschlag]}
      </em>
    );
  }
  return <span className="hilfe">ohne Kategorie</span>;
}

/** SUP-48: Der Kunde hat einen offenen Fall aus einer Terminabsage — sichtbar, bevor man liest. */
export function Absagemarke({ absage }: { readonly absage: Absagehinweis | null }): JSX.Element | null {
  if (absage === null) return null;
  const termin = `${absage.produkt} am ${alsTag(absage.datum) ?? absage.datum}`;
  return (
    <span className={markenklasse('sackgasse')} title={`Abgesagter Termin: ${termin}`}>
      Terminabsage: {termin}
    </span>
  );
}

/** SUP-22: Jemand anderes hat den Vorgang gerade offen. */
export function Bearbeitetmarke({ von }: { readonly von: string | null }): JSX.Element | null {
  if (von === null) return null;
  return <span className={markenklasse('laufend')}>wird bearbeitet von {von}</span>;
}

/** SUP-17: die Fälligkeit; überschritten rot, solange der Vorgang offen ist. */
export function Faelligkeit({ am, offen }: { readonly am: string | null; readonly offen: boolean }): JSX.Element {
  if (am === null) return <span className="hilfe">—</span>;
  const vorbei = offen && Date.parse(am) < Date.now();
  const text = alsZeitpunkt(am) ?? am;
  return vorbei ? <span className={markenklasse('sackgasse')}>fällig {text}</span> : <span className="liste__einzeilig">{text}</span>;
}
